import type { CSSProperties, FC, ReactNode } from 'react'

import { useCallback, useRef, useState } from 'react'
import { Play } from 'lucide-react'
import Keyboard from '@/components/landing/keyboard'
import SoldOutButton from '@/components/landing/sold-out-button'
import VideoDialog from '@/components/landing/video-dialog'
import { NuPhyMark, RayMark } from '@/components/landing/brand-marks'

import { LAUNCH_VIDEO_ID } from '@/constants/navigation'

const HERO_UNIT = 'clamp(19px, calc((100vw - 24px) / 16.9), 55px)'
const HERO_FADE = 'linear-gradient(to bottom, #000 0%, #000 84%, rgb(0 0 0 / 0.5) 92%, transparent 100%)'

const glowStyle = (side: 'left' | 'right'): CSSProperties => ({
    left: side === 'left' ? 'calc(50% - var(--u) * 11.2)' : 'calc(50% + var(--u) * 5.2)',
    top: '14%',
    width: 'calc(var(--u) * 6)',
    height: 'calc(var(--u) * 5.4)',
    background: 'radial-gradient(closest-side, rgb(255 255 255 / 0.13), rgb(255 255 255 / 0.04) 60%, transparent)'
})

const HeroSection: FC = (): ReactNode => {
    const [videoOpen, setVideoOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)

    const closeVideo = useCallback(() => {
        setVideoOpen(false)
        triggerRef.current?.focus()
    }, [])

    return (
        <section id='hero' className='relative overflow-hidden pt-32 sm:pt-[188px]'>
            <div className='relative z-10 mx-auto flex max-w-[680px] flex-col items-center px-6 text-center'>
                <div
                    role='img'
                    aria-label='Raycast × NuPhy'
                    className='flex items-center gap-2.5 text-[#7d7e82] animate-in fade-in duration-700'
                >
                    <RayMark className='size-8' />

                    <span className='text-[11px] text-[#5d5e62]'>
                        ×
                    </span>

                    <NuPhyMark className='text-[16px]' />
                </div>

                <h1 className='mt-5 text-[40px] leading-[1.05] font-medium tracking-[-0.035em] text-white animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-700 sm:text-[54px]'>
                    The Raycast Keyboard
                </h1>

                <p className='mt-5 max-w-[430px] text-[17px] leading-[1.6] text-[#9c9ca0] animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-100 duration-700 sm:text-[18px]'>
                    A low-profile mechanical keyboard designed for instant access to your favorite commands.
                </p>

                <div className='mt-10 flex flex-wrap items-center justify-center gap-3 animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-200 duration-700'>
                    <SoldOutButton />

                    <button
                        ref={triggerRef}
                        type='button'
                        onClick={() => setVideoOpen(true)}
                        className='inline-flex h-9 items-center gap-2 rounded-lg border border-white/20 bg-[#0d0e10] px-3.5 text-[14px] font-medium text-white transition-colors hover:border-white/35 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
                    >
                        <span className='grid size-4 place-items-center rounded-full bg-white'>
                            <Play className='ml-px size-2 fill-[#0d0e10] text-[#0d0e10]' />
                        </span>
                        Watch video
                    </button>
                </div>
            </div>

            <div
                className='relative mt-12 animate-in fade-in fill-mode-both delay-300 duration-1000 sm:mt-10'
                style={{ '--u': HERO_UNIT } as CSSProperties}
            >
                <div aria-hidden='true' className='pointer-events-none absolute blur-2xl' style={glowStyle('left')} />
                <div aria-hidden='true' className='pointer-events-none absolute blur-2xl' style={glowStyle('right')} />

                <div style={{ maskImage: HERO_FADE, WebkitMaskImage: HERO_FADE }}>
                    <Keyboard
                        unit={HERO_UNIT}
                        tilt={32}
                        perspective={37}
                        interactive
                        lighting
                        label='The Raycast Keyboard: a dark low-profile 75% keyboard with an orange Hyper key, orange function icons, a rotary knob and red light bars'
                        className='mx-auto'
                    />
                </div>
            </div>

            <VideoDialog
                open={videoOpen}
                videoId={LAUNCH_VIDEO_ID}
                title='Introducing the NuPhy × Raycast Keyboard'
                onClose={closeVideo}
            />
        </section>
    )
}

export default HeroSection
